import { REDIRECT_IN_PROGRESS, type AuthProvider, type AuthSession, type MfaProvider } from './AuthProvider'
import type { User } from '@/types/user'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithCustomToken,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  type User as FirebaseUser,
} from 'firebase/auth'
import { auth } from './firebase'
import { identityProviderName, mfaService } from './MfaService'
import { logUnexpectedFailure } from '@/utils/errorHandling'
import { firebaseErrorCode } from '@/utils/firebaseErrorCode'

/**
 * Firebase auth error codes that mean the session is over for good: the *refresh*
 * token is gone, so no amount of `getIdToken(true)` can recover it. Anything else
 * (network hiccups, `auth/network-request-failed`, a stale ID token) is retryable
 * and must keep its existing error handling.
 */
const TERMINAL_AUTH_ERROR_CODES = [
  'auth/user-token-expired',
  'auth/user-token-revoked',
  'auth/user-disabled',
]

export class FirebaseAuthService implements AuthProvider {
  readonly mfa: MfaProvider = mfaService

  private isMobileDevice(): boolean {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  }

  private async mapFirebaseUser(firebaseUser: FirebaseUser | null): Promise<User | null> {
    if (!firebaseUser) return null

    // Get ID token result to access custom claims
    try {
      const idTokenResult = await firebaseUser.getIdTokenResult()
      const customClaims = idTokenResult.claims

      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        organizationId: customClaims.organizationId as string | undefined,
        emailVerified: firebaseUser.emailVerified,
      }
    } catch (error) {
      logUnexpectedFailure('Failed to get ID token result:', error)
      // Fallback to basic user info without custom claims
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        emailVerified: firebaseUser.emailVerified,
      }
    }
  }

  async signIn(email: string, password: string): Promise<User> {
    const userCredential = await signInWithEmailAndPassword(auth, email, password)
    const user = await this.mapFirebaseUser(userCredential.user)
    if (!user) {
      throw new Error('Failed to sign in')
    }
    return user
  }

  async signUp(email: string, password: string): Promise<User> {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    // Firebase does not send a verification email automatically; trigger it so the
    // emailVerified gate (e.g. MFA enrollment) can actually be satisfied. A failure
    // here (e.g. rate limiting) must not fail the signup — the account already exists
    // and the user can resend verification later (MfaSettingsCard / EmailVerificationBanner).
    try {
      await sendEmailVerification(userCredential.user)
    } catch (e) {
      console.warn('Failed to send verification email after signup:', e)
    }
    const user = await this.mapFirebaseUser(userCredential.user)
    if (!user) {
      throw new Error('Failed to create account')
    }
    return user
  }

  async signInWithSSO(provider: string): Promise<User> {
    let authProvider

    switch (provider.toLowerCase()) {
      case 'google':
        authProvider = new GoogleAuthProvider()
        break
      case 'microsoft':
        authProvider = new OAuthProvider('microsoft.com')
        // Request additional scopes if needed
        authProvider.addScope('openid')
        authProvider.addScope('email')
        authProvider.addScope('profile')
        break
      default:
        throw new Error(`Unsupported SSO provider: ${provider}`)
    }

    // Use redirect flow on mobile to avoid popup blocking issues
    if (this.isMobileDevice()) {
      await signInWithRedirect(auth, authProvider)
      // The actual sign-in completion happens after redirect
      // getRedirectResult() will be called in initAuthListener
      // We need to throw a specific error to signal redirect is in progress
      throw new Error(REDIRECT_IN_PROGRESS)
    }

    // Desktop: use popup flow
    const userCredential = await signInWithPopup(auth, authProvider)
    const user = await this.mapFirebaseUser(userCredential.user)
    if (!user) {
      throw new Error('Failed to sign in with SSO')
    }
    return user
  }

  async signOut(): Promise<void> {
    await firebaseSignOut(auth)
  }

  async getCurrentUser(): Promise<User | null> {
    return await this.mapFirebaseUser(auth.currentUser)
  }

  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    return firebaseOnAuthStateChanged(auth, async (firebaseUser) => {
      const user = await this.mapFirebaseUser(firebaseUser)
      callback(user)
    })
  }

  currentSession(): AuthSession | null {
    const currentUser = auth.currentUser
    if (!currentUser) return null
    return {
      uid: currentUser.uid,
      email: currentUser.email,
      emailVerified: currentUser.emailVerified,
    }
  }

  async getIdToken(forceRefresh = false): Promise<string | null> {
    const currentUser = auth.currentUser
    if (!currentUser) return null
    return await currentUser.getIdToken(forceRefresh)
  }

  async getTokenClaims(forceRefresh = false): Promise<Record<string, unknown> | null> {
    const currentUser = auth.currentUser
    if (!currentUser) return null
    const tokenResult = await currentUser.getIdTokenResult(forceRefresh)
    return tokenResult.claims
  }

  isSessionExpiredError(error: unknown): boolean {
    const code = firebaseErrorCode(error)
    return code !== undefined && TERMINAL_AUTH_ERROR_CODES.includes(code)
  }

  async sendEmailVerification(): Promise<void> {
    const currentUser = auth.currentUser
    if (!currentUser) return
    await sendEmailVerification(currentUser)
  }

  async signInWithCustomToken(token: string): Promise<void> {
    await signInWithCustomToken(auth, token)
  }

  isSignInLink(url: string): boolean {
    return isSignInWithEmailLink(auth, url)
  }

  async signInWithEmailLink(email: string, url: string): Promise<void> {
    await signInWithEmailLink(auth, email, url)
  }

  async handleRedirectResult(): Promise<User | null> {
    try {
      const result = await getRedirectResult(auth)
      if (!result) return null
      const user = await this.mapFirebaseUser(result.user)
      return user && { ...user, identityProvider: identityProviderName(result.providerId) }
    } catch (error) {
      // An MFA-enrolled user completing the mobile SSO redirect surfaces here as
      // auth/multi-factor-auth-required. That is an expected step of the sign-in
      // flow, not a failure, so it is not logged as an error. The original error
      // (with its code) is always rethrown unwrapped so the caller can present
      // the MFA challenge. See auth store initAuthListener.
      if (!mfaService.isMfaRequiredError(error)) {
        logUnexpectedFailure('Failed to handle redirect result:', error)
      }
      throw error
    }
  }
}
